import React from 'react';
import {
  Box,
  Paper,
  Typography,
  Stack,
  Button,
  TextField,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  Chip,
  IconButton,
  Tooltip
} from '@mui/material';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { getFacultyCourses, createCourse, updateCourse, deleteCourse } from '../../services/courses.api';
import { useAuth } from '../../context/AuthContext';
import { COURSE_DEPARTMENTS, COURSE_SEMESTERS, COURSE_YEARS, toRoman } from '../../utils/courseConstants';

const emptyForm = { code: '', name: '', description: '', department: '', year: '', semester: '', status: 'ACTIVE' };

const MyCourses = () => {
  const [rows, setRows] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState('');
  const { user } = useAuth();
  const isFaculty = user?.role === 'faculty';
  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [editRow, setEditRow] = React.useState(null);
  const [form, setForm] = React.useState(emptyForm);
  const [saving, setSaving] = React.useState(false);

  const load = React.useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await getFacultyCourses();
      setRows(Array.isArray(data) ? data : []);
    } catch (e) {
      setError(e?.response?.data?.message || 'Failed to load courses');
    } finally {
      setLoading(false);
    }
  }, []);

  const statusChipProps = (status) => {
    switch (status) {
      case "ACTIVE":
        return { label: "Active", color: "success" };
      case "INACTIVE":
        return { label: "Inactive", color: "default" };
      default:
        return { label: status, color: "default" };
    }
  };

  const inferYearSemesterFromCourseCode = React.useCallback((rawCode) => {
    const code = String(rawCode || '').trim().toUpperCase();

    // Expect something like CS61 / IT42 / EE31 (two letters followed by digits)
    const m = code.match(/^[A-Z]{2}(\d{2})/);
    if (!m) return null;

    const n = Number(m[1]); // e.g. 61
    if (!Number.isFinite(n) || n < 1) return null;

    // 1-2 => Year 1, 3-4 => Year 2, 5-6 => Year 3, 7-8 => Year 4
    const year = Math.ceil(n / 2);
    if (year < 1 || year > 4) return null;

    // odd => sem 1, even => sem 2
    const semester = (n % 2 === 1) ? 1 : 2;

    return { year, semester };
  }, []);

  React.useEffect(() => {
    if (isFaculty) load();
    else setLoading(false);
  }, [load, isFaculty, user]);

  const openCreate = () => {
    if (!isFaculty) return;
    setEditRow(null);
    setForm(emptyForm);
    setDialogOpen(true);
  };

  const openEdit = (row) => {
    if (!isFaculty) return;
    setEditRow(row);
    setForm({
      code: row.courseId || '',
      name: row.title || '',
      description: row.description || '',
      department: row.department || '',
      year: row.year ?? '',
      semester: row.semester ?? '',
      status: row.status || 'ACTIVE'
    });
    setDialogOpen(true);
  };

  const onSave = async () => {
    if (!isFaculty) return;
    if (!form.code.trim() || !form.name.trim()) {
      setError('code and name are required');
      return;
    }

    const payload = {
      courseId: form.code,
      title: form.name,
      description: form.description,
      department: form.department,
      year: form.year === '' ? undefined : Number(form.year),
      semester: form.semester === '' ? undefined : Number(form.semester),
      status: form.status
    };

    setSaving(true);
    setError('');
    try {
      if (editRow?._id) {
        await updateCourse(editRow._id, payload);
      } else {
        await createCourse(payload);
      }
      setDialogOpen(false);
      await load();
    } catch (e) {
      setError(e?.response?.data?.message || 'Failed to save course');
    } finally {
      setSaving(false);
    }
  };

  const onDelete = async (row) => {
    if (!isFaculty) return;
    setError('');
    try {
      await deleteCourse(row._id);
      await load();
    } catch (e) {
      setError(e?.response?.data?.message || 'Failed to delete course');
    }
  };

  return (
    <Box sx={{ p: 0 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="h5" sx={{ fontWeight: 600 }}>My Courses</Typography>
        {isFaculty && <Button variant="contained" onClick={openCreate}>Add Course</Button>}
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Paper elevation={1} sx={{ p: 2 }}>
        {loading ? (
          <Typography variant="body2">Loading...</Typography>
        ) : rows.length === 0 ? (
          <Typography variant="body2" color="text.secondary">No courses yet.</Typography>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Code</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Department</TableCell>
                <TableCell>Year</TableCell>
                <TableCell>Semester</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Description</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => (
                <TableRow key={row._id} hover>
                  <TableCell>{row.courseId}</TableCell>
                  <TableCell>{row.title}</TableCell>
                  <TableCell>{row.department || '-'}</TableCell>
                  <TableCell>{row.year ? toRoman(row.year) : '-'}</TableCell>
                  <TableCell>{row.semester ? toRoman(row.semester) : '-'}</TableCell>
                  <TableCell>
                    <Chip size='small' {...statusChipProps(row?.status) || '-'}/></TableCell>
                  <TableCell>{row.description || '-'}</TableCell>
                  <TableCell align="right">
                    {isFaculty ? (
                      <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                        <Tooltip title="Edit">
                          <IconButton size="small" onClick={() => openEdit(row)} aria-label="edit course">
                            <EditOutlinedIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => onDelete(row)}
                            aria-label="delete course"
                          >
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    ) : (
                      <Typography variant="body2" color="text.secondary">-</Typography>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Paper>

      <Dialog open={dialogOpen && isFaculty} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editRow ? 'Edit Course' : 'Add Course'}</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Code"
              value={form.code}
              onChange={(e) => {
                const nextCode = e.target.value;
                const inferred = inferYearSemesterFromCourseCode(nextCode);

                setForm((p) => ({
                  ...p,
                  code: nextCode,
                  ...(inferred ? { year: inferred.year, semester: inferred.semester } : {})
                }));
              }}
              fullWidth
              required
              helperText="Unique course code (example: CS611PE)"
            />
            <TextField
              label="Name"
              value={form.name}
              onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
              fullWidth
              required
            />
            <TextField
              select
              label="Department"
              value={form.department}
              onChange={(e) => setForm((p) => ({ ...p, department: e.target.value }))}
              fullWidth
            >
              <MenuItem value=""><em>None</em></MenuItem>
              {COURSE_DEPARTMENTS.map((d) => (
                <MenuItem key={d} value={d}>{d}</MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label="Year"
              value={form.year}
              onChange={(e) => setForm((p) => ({ ...p, year: e.target.value }))}
              fullWidth
            >
              <MenuItem value=""><em>None</em></MenuItem>
              {COURSE_YEARS.map((y) => (
                <MenuItem key={y} value={y}>{toRoman(y)}</MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label="Semester"
              value={form.semester}
              onChange={(e) => setForm((p) => ({ ...p, semester: e.target.value }))}
              fullWidth
            >
              <MenuItem value=""><em>None</em></MenuItem>
              {COURSE_SEMESTERS.map((s) => (
                <MenuItem key={s} value={s}>{toRoman(s)}</MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label="Status"
              value={form.status}
              onChange={(e) => setForm((p) => ({ ...p, status: e.target.value }))}
              fullWidth
            >
              <MenuItem value="ACTIVE">ACTIVE</MenuItem>
              <MenuItem value="INACTIVE">INACTIVE</MenuItem>
            </TextField>
            <TextField
              label="Description"
              value={form.description}
              onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
              fullWidth
              multiline
              minRows={3}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} disabled={saving}>Cancel</Button>
          <Button variant="contained" onClick={onSave} disabled={saving}>
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MyCourses;
